<template>
  <div class="messaging-layout">
    <!-- Sidebar -->
    <DemoSidebar :isAgent="isAgent" />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <DemoTopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="messaging-container">
          <div class="messaging-layout-inner">
            <!-- Conversations List -->
            <div class="conversations-container" :class="{ 'mobile-hidden': selectedConversation && isMobile }">

              <div v-if="loading" class="loading-state">
                <div class="loading-spinner"></div>
                <p>Loading conversations...</p>
              </div>

              <div v-else-if="conversations.length === 0" class="empty-state">
                <div class="empty-icon">💬</div>
                <h3>No conversations yet</h3>
                <p>Start a conversation to see it here</p>
              </div>

              <div v-else class="conversations-list">
                <div
                  v-for="conversation in conversations"
                  :key="conversation.conversation_key"
                  class="conversation-item"
                  :class="{
                    'has-unread': conversation.unread_count > 0,
                    'selected': selectedConversation && selectedConversation.other_id === conversation.other_id
                  }"
                  @click="openConversation(conversation)"
                >
                  <div class="conversation-avatar">
                    <div class="avatar-circle" :class="getAvatarClass(conversation.other_name)">
                      {{ getInitials(conversation.other_name) }}
                    </div>
                    <div v-if="isOnline(conversation.other_id)" class="online-indicator"></div>
                  </div>

                  <div class="conversation-content">
                    <div class="conversation-header">
                      <h4 class="contact-name">{{ conversation.other_name }}</h4>
                      <div class="conversation-meta">
                        <span v-if="conversation.unread_count > 0" class="unread-badge">
                          {{ conversation.unread_count }}
                        </span>
                        <span class="timestamp">{{ formatTime(conversation.last_message_time) }}</span>
                      </div>
                    </div>

                    <div class="last-message">
                      <span v-if="conversation.is_sent_by_me" class="message-indicator">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                        You:
                      </span>
                      <span class="message-text">{{ conversation.last_message || 'No messages yet' }}</span>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <!-- Conversation View -->
            <div v-if="selectedConversation" class="conversation-view">
              <div class="conversation-header">
                <div class="conversation-info">
                  <div class="avatar-circle" :class="getAvatarClass(selectedConversation.other_name)">
                    {{ getInitials(selectedConversation.other_name) }}
                  </div>
                  <div class="contact-details">
                    <h3>{{ selectedConversation.other_name }}</h3>
                    <span class="contact-status" :class="{ online: isOnline(selectedConversation.other_id) }">
                      {{ isOnline(selectedConversation.other_id) ? 'Online' : 'Offline' }}
                    </span>
                  </div>
                </div>
                <button @click="closeConversation" class="close-btn" :title="isMobile ? 'Back to conversations' : 'Close conversation'">
                  <svg v-if="isMobile" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.42-1.41L7.83 13H20v-2z"/>
                  </svg>
                  <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              </div>

              <div class="messages-container" ref="messagesContainer">
                <div class="messages-list">
                  <div v-if="currentConversationMessages.length === 0" class="no-messages">
                    <div class="no-messages-icon">💬</div>
                    <p>No messages yet. Start the conversation!</p>
                  </div>

                  <div
                    v-for="message in sortedMessages"
                    :key="message.id"
                    class="message"
                    :class="{ 'sent': message.is_sent_by_me, 'received': !message.is_sent_by_me }"
                  >
                    <div class="message-content">
                      <div class="message-text">{{ message.message }}</div>
                      <div class="message-meta">
                        <span class="message-time">{{ formatMessageTime(message.time) }}</span>
                        <span v-if="message.is_sent_by_me && !message.is_read" class="message-status unread">Sent</span>
                        <span v-else-if="message.is_sent_by_me && message.is_read" class="message-status read">Read</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="message-input-container">
                <form @submit.prevent="sendMessage" class="message-form">
                  <div class="input-wrapper">
                    <textarea
                      v-model="newMessage"
                      placeholder="Type your message..."
                      class="message-input"
                      rows="1"
                      @keydown="handleKeyDown"
                      ref="messageInput"
                    ></textarea>
                    <button
                      type="submit"
                      class="send-btn"
                      :disabled="!newMessage.trim() || sendingMessage"
                      title="Send message"
                    >
                      <svg v-if="sendingMessage" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="sending-icon">
                        <path d="M12 4V2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 16.17l7.59-7.59L19 10l-9 9z"/>
                      </svg>
                      <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                      </svg>
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <!-- No Conversation Selected -->
            <div v-else class="no-conversation-selected">
              <div class="placeholder-content">
                <div class="placeholder-icon">💬</div>
                <h3>Select a conversation</h3>
                <p>Choose a conversation from the list to start messaging</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import DemoSidebar from '../components/DemoSidebar.vue'
import DemoTopHeader from '../components/DemoTopHeader.vue'

export default {
  name: 'DemoMessagingView',
  components: {
    DemoSidebar,
    DemoTopHeader
  },
  data () {
    return {
      isAgent: !!localStorage.getItem('agent_token'),
      conversations: [],
      loading: false,
      onlineUsers: [1, 3, 5, 7, 9, 11, 15], // Mock online status
      refreshInterval: null,
      selectedConversation: null,
      currentConversationMessages: [],
      loadingMessages: false,
      newMessage: '',
      sendingMessage: false,
      conversationRefreshInterval: null,
      isMobile: false,
      // Dummy data storage
      allMessages: {}
    }
  },
  computed: {
    sortedMessages () {
      // Sort messages by time (oldest first, newest last) for proper chat order
      return [...this.currentConversationMessages].sort((a, b) => {
        const timeA = new Date(a.time).getTime()
        const timeB = new Date(b.time).getTime()
        return timeA - timeB
      })
    }
  },
  async mounted () {
    await this.loadConversations()
    this.startAutoRefresh()
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeUnmount () {
    this.stopAutoRefresh()
    this.stopConversationRefresh()
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    async loadConversations (isAutoRefresh = false) {
      try {
        if (!isAutoRefresh) {
          this.loading = true
        }

        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 300))

        // Generate dummy conversations with Egyptian names
        this.conversations = this.generateDummyConversations()
        this.generateDummyMessages()
      } catch (error) {
        console.error('Error loading conversations:', error)
        if (!isAutoRefresh) {
          alert('Error loading conversations: ' + error.message)
        }
      } finally {
        if (!isAutoRefresh) {
          this.loading = false
        }
      }
    },

    generateDummyConversations () {
      const now = new Date()

      return [
        {
          conversation_key: 'conv_1',
          other_id: 1,
          other_name: 'Ahmed Mohamed',
          last_message: 'Can you follow up with the customer about the pending quote?',
          last_message_time: new Date(now - 5 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          unread_count: 2
        },
        {
          conversation_key: 'conv_2',
          other_id: 2,
          other_name: 'Fatma Hassan',
          last_message: 'Great! The client just confirmed the meeting for tomorrow.',
          last_message_time: new Date(now - 15 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          unread_count: 0
        },
        {
          conversation_key: 'conv_3',
          other_id: 3,
          other_name: 'Mohamed Hassan',
          last_message: 'I need help with the CRM integration, can you assist?',
          last_message_time: new Date(now - 30 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          unread_count: 1
        },
        {
          conversation_key: 'conv_4',
          other_id: 4,
          other_name: 'Nour Ahmed',
          last_message: 'Perfect, I will schedule that for next week.',
          last_message_time: new Date(now - 2 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          unread_count: 0
        },
        {
          conversation_key: 'conv_5',
          other_id: 5,
          other_name: 'Yasmin Mohamed',
          last_message: 'The report is ready for your review.',
          last_message_time: new Date(now - 3 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          unread_count: 3
        },
        {
          conversation_key: 'conv_6',
          other_id: 6,
          other_name: 'Omar Khalil',
          last_message: 'Thanks for the update, will check it now.',
          last_message_time: new Date(now - 4 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          unread_count: 0
        },
        {
          conversation_key: 'conv_7',
          other_id: 7,
          other_name: 'Sara Ali',
          last_message: 'Client called about changing the delivery date.',
          last_message_time: new Date(now - 5 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          unread_count: 1
        },
        {
          conversation_key: 'conv_8',
          other_id: 8,
          other_name: 'Mahmoud Ali',
          last_message: 'All set for the presentation tomorrow!',
          last_message_time: new Date(now - 6 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          unread_count: 0
        },
        {
          conversation_key: 'conv_9',
          other_id: 9,
          other_name: 'Heba Mostafa',
          last_message: 'Could you send me the latest sales numbers?',
          last_message_time: new Date(now - 1 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          unread_count: 0
        },
        {
          conversation_key: 'conv_10',
          other_id: 10,
          other_name: 'Karim Ibrahim',
          last_message: 'Meeting went well, they are interested!',
          last_message_time: new Date(now - 1 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          unread_count: 0
        },
        {
          conversation_key: 'conv_11',
          other_id: 11,
          other_name: 'Mona Sayed',
          last_message: 'Can you review the contract before I send it?',
          last_message_time: new Date(now - 2 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          unread_count: 2
        },
        {
          conversation_key: 'conv_12',
          other_id: 12,
          other_name: 'Tamer Farid',
          last_message: 'Done! Updated all the customer records.',
          last_message_time: new Date(now - 2 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          unread_count: 0
        },
        {
          conversation_key: 'conv_13',
          other_id: 13,
          other_name: 'Aya Gamal',
          last_message: 'The training session is scheduled for Thursday.',
          last_message_time: new Date(now - 3 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          unread_count: 0
        },
        {
          conversation_key: 'conv_14',
          other_id: 14,
          other_name: 'Hassan Omar',
          last_message: 'I finished the analysis, sending it over now.',
          last_message_time: new Date(now - 3 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          unread_count: 0
        },
        {
          conversation_key: 'conv_15',
          other_id: 15,
          other_name: 'Dina Khaled',
          last_message: 'Thanks for your help with the customer issue!',
          last_message_time: new Date(now - 4 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          unread_count: 0
        },
        {
          conversation_key: 'conv_16',
          other_id: 16,
          other_name: 'Youssef Adel',
          last_message: 'Customer needs a quote for 50 units.',
          last_message_time: new Date(now - 5 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          unread_count: 0
        },
        {
          conversation_key: 'conv_17',
          other_id: 17,
          other_name: 'Mariam Nabil',
          last_message: 'I will handle the escalation from this morning.',
          last_message_time: new Date(now - 6 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          unread_count: 0
        },
        {
          conversation_key: 'conv_18',
          other_id: 18,
          other_name: 'Amr Sherif',
          last_message: 'Let me know when you are free for a quick call.',
          last_message_time: new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          unread_count: 0
        }
      ]
    },

    generateDummyMessages () {
      const now = new Date()

      // Messages for Ahmed Mohamed (other_id: 1)
      this.allMessages[1] = [
        {
          id: 1,
          message: 'Hi Ahmed, how is the follow-up going with the new client?',
          time: new Date(now - 2 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 2,
          message: 'Going well! They requested some changes to the quote.',
          time: new Date(now - 90 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        },
        {
          id: 3,
          message: 'Can you send me the updated requirements?',
          time: new Date(now - 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 4,
          message: 'Sure, will send them in 10 minutes.',
          time: new Date(now - 55 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        },
        {
          id: 5,
          message: 'Can you follow up with the customer about the pending quote?',
          time: new Date(now - 5 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: false
        }
      ]

      // Messages for Fatma Hassan (other_id: 2)
      this.allMessages[2] = [
        {
          id: 6,
          message: 'Fatma, did the client confirm the meeting time?',
          time: new Date(now - 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 7,
          message: 'Yes! They confirmed 2 PM tomorrow.',
          time: new Date(now - 45 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        },
        {
          id: 8,
          message: 'Perfect! I will prepare the presentation.',
          time: new Date(now - 30 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 9,
          message: 'Great! The client just confirmed the meeting for tomorrow.',
          time: new Date(now - 15 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        }
      ]

      // Messages for Mohamed Hassan (other_id: 3)
      this.allMessages[3] = [
        {
          id: 10,
          message: 'Mohamed, are you familiar with the Odoo integration?',
          time: new Date(now - 3 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 11,
          message: 'Yes, I worked on it last month.',
          time: new Date(now - 2.5 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        },
        {
          id: 12,
          message: 'I need help with the CRM integration, can you assist?',
          time: new Date(now - 30 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: false
        }
      ]

      // Messages for Nour Ahmed (other_id: 4)
      this.allMessages[4] = [
        {
          id: 13,
          message: 'Nour, can you schedule the team meeting?',
          time: new Date(now - 4 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 14,
          message: 'When would you like to have it?',
          time: new Date(now - 3.5 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        },
        {
          id: 15,
          message: 'Next week Wednesday would be good.',
          time: new Date(now - 3 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 16,
          message: 'Perfect, I will schedule that for next week.',
          time: new Date(now - 2 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        }
      ]

      // Messages for Yasmin Mohamed (other_id: 5)
      this.allMessages[5] = [
        {
          id: 17,
          message: 'Yasmin, how is the monthly report coming along?',
          time: new Date(now - 5 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 18,
          message: 'Almost done, just adding the final charts.',
          time: new Date(now - 4.5 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        },
        {
          id: 19,
          message: 'The report is ready for your review.',
          time: new Date(now - 3 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: false
        }
      ]

      // Messages for Omar Khalil (other_id: 6)
      this.allMessages[6] = [
        {
          id: 20,
          message: 'Omar, I sent you the updated pricing list.',
          time: new Date(now - 6 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 21,
          message: 'Thanks for the update, will check it now.',
          time: new Date(now - 4 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        }
      ]

      // Messages for Sara Ali (other_id: 7)
      this.allMessages[7] = [
        {
          id: 22,
          message: 'Sara, any updates from the delivery team?',
          time: new Date(now - 8 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 23,
          message: 'Everything is on track for this week.',
          time: new Date(now - 7 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        },
        {
          id: 24,
          message: 'Client called about changing the delivery date.',
          time: new Date(now - 5 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: false
        }
      ]

      // Messages for Mahmoud Ali (other_id: 8)
      this.allMessages[8] = [
        {
          id: 25,
          message: 'Mahmoud, are you ready for tomorrow\'s presentation?',
          time: new Date(now - 10 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 26,
          message: 'Yes, just finalizing the slides.',
          time: new Date(now - 9 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        },
        {
          id: 27,
          message: 'All set for the presentation tomorrow!',
          time: new Date(now - 6 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        }
      ]

      // Messages for Heba Mostafa (other_id: 9)
      this.allMessages[9] = [
        {
          id: 28,
          message: 'Heba, I need the Q4 sales data.',
          time: new Date(now - 2 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 29,
          message: 'Could you send me the latest sales numbers?',
          time: new Date(now - 1 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        }
      ]

      // Messages for Karim Ibrahim (other_id: 10)
      this.allMessages[10] = [
        {
          id: 30,
          message: 'Karim, how did the client meeting go?',
          time: new Date(now - 2 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 31,
          message: 'Meeting went well, they are interested!',
          time: new Date(now - 1 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        }
      ]

      // Messages for Mona Sayed (other_id: 11)
      this.allMessages[11] = [
        {
          id: 32,
          message: 'Mona, I updated the contract template.',
          time: new Date(now - 3 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 33,
          message: 'Can you review the contract before I send it?',
          time: new Date(now - 2 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: false
        }
      ]

      // Messages for Tamer Farid (other_id: 12)
      this.allMessages[12] = [
        {
          id: 34,
          message: 'Tamer, can you update the customer database?',
          time: new Date(now - 4 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 35,
          message: 'Sure, will do it today.',
          time: new Date(now - 3.5 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        },
        {
          id: 36,
          message: 'Done! Updated all the customer records.',
          time: new Date(now - 2 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        }
      ]

      // Messages for Aya Gamal (other_id: 13)
      this.allMessages[13] = [
        {
          id: 37,
          message: 'Aya, when is the next training session?',
          time: new Date(now - 4 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 38,
          message: 'The training session is scheduled for Thursday.',
          time: new Date(now - 3 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        }
      ]

      // Messages for Hassan Omar (other_id: 14)
      this.allMessages[14] = [
        {
          id: 39,
          message: 'Hassan, did you complete the data analysis?',
          time: new Date(now - 5 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 40,
          message: 'I finished the analysis, sending it over now.',
          time: new Date(now - 3 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        }
      ]

      // Messages for Dina Khaled (other_id: 15)
      this.allMessages[15] = [
        {
          id: 41,
          message: 'Dina, I resolved the customer complaint.',
          time: new Date(now - 5 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 42,
          message: 'Thanks for your help with the customer issue!',
          time: new Date(now - 4 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        }
      ]

      // Messages for Youssef Adel (other_id: 16)
      this.allMessages[16] = [
        {
          id: 43,
          message: 'Youssef, any new inquiries today?',
          time: new Date(now - 6 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 44,
          message: 'Customer needs a quote for 50 units.',
          time: new Date(now - 5 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        }
      ]

      // Messages for Mariam Nabil (other_id: 17)
      this.allMessages[17] = [
        {
          id: 45,
          message: 'Mariam, there is an escalation that needs attention.',
          time: new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 46,
          message: 'I will handle the escalation from this morning.',
          time: new Date(now - 6 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        }
      ]

      // Messages for Amr Sherif (other_id: 18)
      this.allMessages[18] = [
        {
          id: 47,
          message: 'Amr, we need to discuss the new strategy.',
          time: new Date(now - 8 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: true,
          is_read: true
        },
        {
          id: 48,
          message: 'Let me know when you are free for a quick call.',
          time: new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString(),
          is_sent_by_me: false,
          is_read: true
        }
      ]
    },

    startAutoRefresh () {
      // Refresh every 30 seconds (stubbed - no actual refresh in demo)
      this.refreshInterval = setInterval(async () => {
        // Do nothing in demo mode
      }, 30000)
    },

    stopAutoRefresh () {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
        this.refreshInterval = null
      }
    },

    getInitials (name) {
      return name
        .split(' ')
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2)
    },

    getAvatarClass (name) {
      // Generate consistent color based on name
      const colors = ['avatar-blue', 'avatar-green', 'avatar-purple', 'avatar-orange', 'avatar-pink']
      const index = name.length % colors.length
      return colors[index]
    },

    isOnline (userId) {
      return this.onlineUsers.includes(userId)
    },

    formatTime (timeString) {
      if (!timeString) return ''

      try {
        const date = new Date(timeString)
        const now = new Date()
        const diffInHours = Math.abs(now - date) / 36e5

        if (diffInHours < 1) {
          const minutes = Math.floor(diffInHours * 60)
          return minutes <= 1 ? 'Just now' : `${minutes}m ago`
        } else if (diffInHours < 24) {
          return `${Math.floor(diffInHours)}h ago`
        } else if (diffInHours < 168) { // 7 days
          const days = Math.floor(diffInHours / 24)
          return `${days}d ago`
        } else {
          return date.toLocaleDateString()
        }
      } catch (error) {
        return timeString
      }
    },

    async openConversation (conversation) {
      this.selectedConversation = conversation
      await this.loadConversationMessages(conversation.other_id)
      this.startConversationRefresh()
    },

    closeConversation () {
      this.selectedConversation = null
      this.currentConversationMessages = []
      this.newMessage = ''
      this.stopConversationRefresh()
    },

    async loadConversationMessages (agentId, isBackgroundRefresh = false) {
      try {
        // Only show loading state on initial load, not background refreshes
        if (!isBackgroundRefresh) {
          this.loadingMessages = true
        }

        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 200))

        // Load messages for this conversation
        const wasAtBottom = this.isScrolledToBottom()
        this.currentConversationMessages = this.allMessages[agentId] || []

        this.$nextTick(() => {
          // Only auto-scroll if user was already at bottom or if it's initial load
          if (!isBackgroundRefresh || wasAtBottom) {
            this.scrollToBottom()
          }
        })
      } catch (error) {
        console.error('Error loading conversation messages:', error)
      } finally {
        if (!isBackgroundRefresh) {
          this.loadingMessages = false
        }
      }
    },

    isScrolledToBottom () {
      if (!this.$refs.messagesContainer) return true
      const container = this.$refs.messagesContainer
      const threshold = 50 // pixels from bottom
      return container.scrollTop + container.clientHeight >= container.scrollHeight - threshold
    },

    async sendMessage () {
      if (!this.newMessage.trim() || this.sendingMessage || !this.selectedConversation) {
        return
      }

      try {
        this.sendingMessage = true

        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 300))

        // Create new message
        const newMessageObj = {
          id: Date.now(),
          message: this.newMessage.trim(),
          time: new Date().toISOString(),
          is_sent_by_me: true,
          is_read: false
        }

        // Add the new message to the current conversation
        this.currentConversationMessages.push(newMessageObj)

        // Update the messages store
        if (!this.allMessages[this.selectedConversation.other_id]) {
          this.allMessages[this.selectedConversation.other_id] = []
        }
        this.allMessages[this.selectedConversation.other_id].push(newMessageObj)

        // Update the conversation in the list
        const conv = this.conversations.find(c => c.other_id === this.selectedConversation.other_id)
        if (conv) {
          conv.last_message = this.newMessage.trim()
          conv.last_message_time = new Date().toISOString()
          conv.is_sent_by_me = true
        }

        this.newMessage = ''

        this.$nextTick(() => {
          this.scrollToBottom()
          if (this.$refs.messageInput) {
            this.$refs.messageInput.focus()
          }
        })
      } catch (error) {
        console.error('Error sending message:', error)
        alert('Error sending message: ' + error.message)
      } finally {
        this.sendingMessage = false
      }
    },

    handleKeyDown (event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        this.sendMessage()
      }
    },

    scrollToBottom () {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight
      }
    },

    startConversationRefresh () {
      this.stopConversationRefresh()
      // Refresh conversation messages every 10 seconds when a conversation is open (stubbed in demo)
      this.conversationRefreshInterval = setInterval(async () => {
        // Do nothing in demo mode
      }, 10000)
    },

    stopConversationRefresh () {
      if (this.conversationRefreshInterval) {
        clearInterval(this.conversationRefreshInterval)
        this.conversationRefreshInterval = null
      }
    },

    formatMessageTime (timeString) {
      if (!timeString) return ''

      try {
        // Handle the format "Aug 19, 2025 7:56 PM"
        const date = new Date(timeString)
        const now = new Date()
        const diffInHours = Math.abs(now - date) / 36e5

        if (diffInHours < 1) {
          const minutes = Math.floor(diffInHours * 60)
          return minutes <= 1 ? 'Just now' : `${minutes}m ago`
        } else if (diffInHours < 24) {
          return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        } else {
          return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
        }
      } catch (error) {
        return timeString
      }
    },

    checkMobile () {
      this.isMobile = window.innerWidth <= 768
    }
  }
}
</script>

<style scoped>
.messaging-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  padding: 32px;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  transition: color 0.3s ease;
}

.title-icon {
  color: #3b82f6;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
  transition: color 0.3s ease;
}

.header-stats {
  display: flex;
  gap: 32px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.stat-label {
  font-size: 12px;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: color 0.3s ease;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.conversations-list {
  max-height: calc(100vh - 300px);
  overflow-y: auto;
}

.conversation-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid var(--border-secondary);
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.conversation-item:hover {
  background: var(--bg-tertiary);
  transition: background-color 0.2s ease;
}

.conversation-item.has-unread {
  background: rgba(59, 130, 246, 0.05);
  border-left: 4px solid #3b82f6;
}

.conversation-item.has-unread:hover {
  background: rgba(59, 130, 246, 0.1);
}

.conversation-item.has-unread .contact-name {
  font-weight: 700;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.conversation-item.has-unread .message-text {
  font-weight: 600;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.conversation-avatar {
  position: relative;
  flex-shrink: 0;
}

.avatar-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  color: white;
}

.avatar-blue { background: linear-gradient(135deg, #3b82f6, #1d4ed8); }
.avatar-green { background: linear-gradient(135deg, #10b981, #059669); }
.avatar-purple { background: linear-gradient(135deg, #8b5cf6, #7c3aed); }
.avatar-orange { background: linear-gradient(135deg, #f59e0b, #d97706); }
.avatar-pink { background: linear-gradient(135deg, #ec4899, #db2777); }

.online-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  background: #10b981;
  border: 2px solid var(--bg-primary);
  border-radius: 50%;
  transition: border-color 0.3s ease;
}

.conversation-content {
  flex: 1;
  min-width: 0;
}

.conversation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.contact-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  truncate: true;
  transition: color 0.3s ease;
}

.conversation-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.unread-badge {
  background: #3b82f6;
  color: white;
  font-size: 13px;
  font-weight: 800;
  padding: 5px 9px;
  border-radius: 16px;
  min-width: 24px;
  text-align: center;
  line-height: 1;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
}

.timestamp {
  font-size: 12px;
  color: var(--text-tertiary);
  white-space: nowrap;
  transition: color 0.3s ease;
}

.last-message {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

.message-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #3b82f6;
  flex-shrink: 0;
}

.message-text {
  truncate: true;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.loading-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: var(--text-secondary);
  text-align: center;
  transition: color 0.3s ease;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-primary);
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  transition: border-color 0.3s ease;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-icon {
  font-size: 48px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  transition: color 0.3s ease;
}

.empty-state p {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
  transition: color 0.3s ease;
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
    padding: 20px 16px;
  }

  .header-stats {
    justify-content: center;
    gap: 24px;
  }

  .conversations-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }

  .conversation-item {
    padding: 12px 16px;
  }

  .avatar-circle {
    width: 40px;
    height: 40px;
    font-size: 14px;
  }

  .contact-name {
    font-size: 15px;
  }

  .conversation-actions {
    opacity: 1;
  }
}

/* Messaging Container */
.messaging-container {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  overflow: hidden;
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

/* Two-panel layout */
.messaging-layout-inner {
  display: flex;
  flex: 1;
  height: 100%;
}

.conversations-container {
  flex: 0 0 400px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border-primary);
  transition: border-color 0.3s ease;
}

.conversation-item.selected {
  background: rgba(59, 130, 246, 0.05);
  border-left: 4px solid #3b82f6;
  border-color: #3b82f6;
}

/* Conversation View */
.conversation-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--bg-primary);
  overflow: hidden;
  transition: background-color 0.3s ease;
}

.conversation-view .conversation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-tertiary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.conversation-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.contact-details h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px;
  transition: color 0.3s ease;
}

.contact-status {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
  transition: color 0.3s ease;
}

.contact-status.online {
  color: #10b981;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.close-btn:hover {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
  transform: translateY(-1px);
}

/* Messages */
.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.no-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 16px;
  color: var(--text-secondary);
  text-align: center;
  transition: color 0.3s ease;
}

.no-messages-icon {
  font-size: 48px;
  opacity: 0.5;
}

.message {
  display: flex;
  margin-bottom: 8px;
}

.message.sent {
  justify-content: flex-end;
}

.message.received {
  justify-content: flex-start;
}

.message-content {
  max-width: 70%;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.message.sent .message-content {
  align-items: flex-end;
}

.message.received .message-content {
  align-items: flex-start;
}

.message-text {
  padding: 12px 16px;
  border-radius: 18px;
  word-wrap: break-word;
  line-height: 1.4;
}

.message.sent .message-text {
  background: #3b82f6;
  color: white;
  border-bottom-right-radius: 4px;
}

.message.received .message-text {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border-bottom-left-radius: 4px;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.message-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: var(--text-tertiary);
  padding: 0 4px;
  transition: color 0.3s ease;
}

.message-time {
  font-weight: 500;
}

.message-status.unread {
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

.message-status.read {
  color: #10b981;
}

/* Message Input */
.message-input-container {
  padding: 16px 24px 20px;
  border-top: 1px solid var(--border-primary);
  background: var(--bg-tertiary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.message-form {
  width: 100%;
}

.input-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: var(--bg-primary);
  border-radius: 24px;
  border: 1px solid var(--border-primary);
  transition: all 0.2s;
  min-height: 48px;
}

.input-wrapper:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.message-input {
  flex: 1;
  border: none;
  outline: none;
  resize: none;
  font-size: 14px;
  line-height: 1.4;
  color: var(--text-primary);
  background: transparent;
  max-height: 120px;
  min-height: 24px;
  padding: 8px 0;
  font-family: inherit;
  vertical-align: middle;
  overflow: hidden;
  transition: color 0.3s ease;
}

.message-input::placeholder {
  color: var(--text-tertiary);
  line-height: 1.4;
  transition: color 0.3s ease;
}

.send-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: #3b82f6;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.send-btn:hover:not(:disabled) {
  background: #2563eb;
  transform: scale(1.05);
}

.send-btn:disabled {
  background: var(--border-primary);
  color: var(--text-tertiary);
  cursor: not-allowed;
  transform: none;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.sending-icon {
  animation: spin 1s linear infinite;
}

/* No Conversation Selected */
.no-conversation-selected {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary);
  transition: background-color 0.3s ease;
}

.placeholder-content {
  text-align: center;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

.placeholder-icon {
  font-size: 64px;
  opacity: 0.3;
  margin-bottom: 16px;
}

.placeholder-content h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px;
  transition: color 0.3s ease;
}

.placeholder-content p {
  font-size: 16px;
  margin: 0;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  body {
    overflow-x: hidden;
    width: 100%;
    max-width: 100vw;
  }

  .main-content {
    margin-left: 0;
    padding-top: 64px;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
  }

  .page-content {
    padding: 8px;
    width: 100%;
    box-sizing: border-box;
  }

  .messaging-container {
    height: calc(100vh - 80px);
    border-radius: 8px;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  .messaging-layout-inner {
    flex-direction: column;
    height: 100%;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  /* On mobile, show either conversations OR conversation view, not both */
  .conversations-container {
    flex: 1;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  /* Hide conversations list when a conversation is selected on mobile */
  .conversations-container.mobile-hidden {
    display: none;
  }

  .conversation-view {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  .no-conversation-selected {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Mobile conversation item styling */
  .conversation-item {
    padding: 12px 16px;
    border-bottom: 1px solid #f3f4f6;
    min-height: 60px; /* Ensure good touch target */
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow: hidden;
  }

  .conversation-item:active {
    background: #f0f9ff;
    transition: background 0.1s;
  }

  .conversation-content {
    flex: 1;
    min-width: 0; /* Allow text to truncate */
    overflow: hidden;
  }

  .contact-name {
    font-size: 15px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .last-message {
    width: 100%;
    overflow: hidden;
  }

  .message-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .avatar-circle {
    width: 40px;
    height: 40px;
    font-size: 14px;
    flex-shrink: 0;
  }

  /* Mobile conversation view header */
  .conversation-view .conversation-header {
    padding: 16px;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    position: relative;
  }

  .conversation-info {
    gap: 12px;
  }

  .contact-details h3 {
    font-size: 16px;
  }

  .contact-status {
    font-size: 11px;
  }

  /* Add back button for mobile */
  .close-btn {
    width: 32px;
    height: 32px;
  }

  /* Mobile messages container */
  .messages-container {
    flex: 1;
    padding: 8px 12px;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    width: 100%;
    box-sizing: border-box;
  }

  .messages-list {
    gap: 8px;
    width: 100%;
    max-width: 100%;
  }

  .message {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .message-content {
    max-width: 85%;
    box-sizing: border-box;
    overflow: hidden;
  }

  .message-text {
    padding: 10px 14px;
    font-size: 14px;
    border-radius: 16px;
  }

  .message.sent .message-text {
    border-bottom-right-radius: 4px;
  }

  .message.received .message-text {
    border-bottom-left-radius: 4px;
  }

  .message-meta {
    font-size: 10px;
    gap: 6px;
  }

  /* Mobile message input */
  .message-input-container {
    padding: 8px 12px 12px;
    background: #f9fafb;
    border-top: 1px solid #e5e7eb;
    /* Safe area for devices with home indicator */
    padding-bottom: max(12px, env(safe-area-inset-bottom));
    width: 100%;
    box-sizing: border-box;
  }

  .input-wrapper {
    padding: 6px 10px;
    min-height: 44px; /* Minimum touch target size */
    border-radius: 22px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    width: 100%;
    box-sizing: border-box;
  }

  .message-input {
    font-size: 16px; /* Prevent zoom on iOS */
    min-height: 22px;
    padding: 4px 0;
    line-height: 1.4;
    width: 100%;
    box-sizing: border-box;
  }

  .send-btn {
    width: 32px;
    height: 32px;
  }

  /* Mobile loading and empty states */
  .loading-state,
  .empty-state {
    padding: 32px 16px;
  }

  .loading-state p {
    font-size: 14px;
  }

  .empty-state h3 {
    font-size: 18px;
  }

  .empty-state p {
    font-size: 14px;
  }

  /* No messages state for mobile */
  .no-messages {
    height: 150px;
    gap: 12px;
  }

  .no-messages-icon {
    font-size: 36px;
  }

  .no-messages p {
    font-size: 14px;
  }

  /* Conversations list mobile fixes */
  .conversations-list {
    max-height: none;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
  }

  /* Loading and empty states */
  .loading-state,
  .empty-state {
    width: 100%;
    box-sizing: border-box;
  }
}
</style>
